import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimple, Key, Users } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const DEFAULT_TECH_PWD = "Tech@123";

export default function BulkPasswordDialog({ open, onClose, onSaved }) {
  const [tab, setTab] = useState("apply-all");
  const handleDone = () => { onSaved?.(); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900" data-testid="bulk-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Key size={22} weight="duotone" className="text-amber-600" />
            Bulk Set Technician Passwords
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-none border border-neutral-900 bg-white h-auto p-0 w-full">
            <TabsTrigger
              value="apply-all"
              className="flex-1 rounded-none data-[state=active]:bg-neutral-900 data-[state=active]:text-white text-xs font-mono uppercase tracking-wider py-2"
              data-testid="pwd-tab-apply-all"
            >
              <Users size={14} className="mr-1.5" /> Apply to All
            </TabsTrigger>
            <TabsTrigger
              value="csv"
              className="flex-1 rounded-none data-[state=active]:bg-neutral-900 data-[state=active]:text-white text-xs font-mono uppercase tracking-wider py-2"
              data-testid="pwd-tab-csv"
            >
              <UploadSimple size={14} className="mr-1.5" /> Bulk Upload (CSV)
            </TabsTrigger>
          </TabsList>
          <TabsContent value="apply-all" className="mt-4">
            <ApplyAllPanel onDone={handleDone} />
          </TabsContent>
          <TabsContent value="csv" className="mt-4">
            <CsvPanel onDone={handleDone} onSaved={onSaved} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ApplyAllPanel({ onDone }) {
  const [password, setPassword] = useState(DEFAULT_TECH_PWD);
  const [confirm, setConfirm] = useState(DEFAULT_TECH_PWD);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!window.confirm(`This will OVERWRITE every technician's password with "${password}". Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/engineers/bulk-set-password", { password, overwrite: true });
      toast.success(`Password applied to ${data.updated} of ${data.total} technicians`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        This overwrites <b>every</b> technician's password. They'll need to sign in with this new password next time.
      </div>
      <div className="space-y-1.5">
        <Label className="label-eyebrow">New Password</Label>
        <Input
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-none border-neutral-400 font-mono"
          data-testid="pwd-input-password"
        />
        <div className="text-[10px] text-neutral-500">Default is <span className="font-mono">{DEFAULT_TECH_PWD}</span> — edit to any value ≥ 4 chars.</div>
      </div>
      <div className="space-y-1.5">
        <Label className="label-eyebrow">Confirm Password</Label>
        <Input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="rounded-none border-neutral-400 font-mono"
          data-testid="pwd-input-confirm"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="pwd-apply-all-submit"
          className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
        >
          {busy ? "Applying…" : "Apply to All Technicians"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function CsvPanel({ onDone, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseCsv = (raw) => {
    const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    // Detect header row
    const startIdx = /identifier|username|employee|email/i.test(lines[0]) ? 1 : 0;
    return lines.slice(startIdx).map((line) => {
      const parts = line.split(",").map((p) => p.trim());
      return { identifier: parts[0] || "", password: parts[1] || "" };
    });
  };

  const submit = async () => {
    const rows = parseCsv(text);
    if (!rows.length) { toast.error("No rows found. Format: identifier,password (one per line)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/engineers/bulk-set-passwords-csv", { rows });
      setResult(data);
      onSaved?.();
      toast.success(`Updated ${data.updated} of ${data.total} technicians${data.skipped.length ? ` — ${data.skipped.length} skipped` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const raw = await file.text();
    setText(raw);
  };

  return (
    <div className="space-y-3">
      <div className="border border-neutral-300 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
        <div className="font-medium mb-1">Format</div>
        <div className="font-mono text-[11px] whitespace-pre">identifier,password
ff583,MyPass@2026
E12345,Tech@2026
tech@company.com,Winter@25</div>
        <div className="mt-2 text-neutral-500">
          Identifier can be <b>username</b>, <b>employee ID</b>, or <b>email</b>. Header row is optional.
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="label-eyebrow">Upload CSV file</Label>
        <Input
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="rounded-none border-neutral-400"
          data-testid="pwd-csv-file"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="label-eyebrow">Or paste CSV</Label>
        <Textarea
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="ff583,Tech@123&#10;E12345,Winter@25"
          className="rounded-none border-neutral-400 font-mono text-xs"
          data-testid="pwd-csv-text"
        />
      </div>
      {result && (
        <div className="border border-neutral-300 bg-white p-3 text-xs" data-testid="pwd-csv-result">
          <div><b>Updated:</b> {result.updated} · <b>Total rows:</b> {result.total} · <b>Skipped:</b> {result.skipped?.length || 0}</div>
          {!!result.skipped?.length && (
            <ul className="mt-2 space-y-0.5 text-red-800 max-h-40 overflow-y-auto">
              {result.skipped.map((s, i) => (
                <li key={i}>· <span className="font-mono">{s.identifier || "(blank)"}</span> — {s.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onDone} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Close</Button>
        <Button
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid="pwd-csv-submit"
          className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
        >
          {busy ? "Applying…" : "Apply CSV"}
        </Button>
      </DialogFooter>
    </div>
  );
}
